import React, { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '../context/AuthContext';
import api from '../api';
import useDebounce from '../hooks/useDebounce';
import {
  IoSendOutline,
  IoPeopleOutline,
  IoPersonOutline,
  IoTrashOutline,
  IoMailOpenOutline,
  IoMailOutline,
  IoCheckmarkDoneOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoSearchOutline,
  IoCloseCircleOutline,
} from 'react-icons/io5';
import Spinner from './Spinner';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const CLIENT_SEARCH_MIN_CHARS = 2;
const CLIENT_SEARCH_PREVIEW_LIMIT = 8;

// ─── Coach view ──────────────────────────────────────────────────────────────

function CoachMessages() {
  const [clients, setClients] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchingClients, setSearchingClients] = useState(false);

  const [form, setForm] = useState({ recipient_id: '', subject: '', body: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const debouncedClientSearch = useDebounce(clientSearch, 250);

  const fetchData = useCallback(async () => {
    try {
      const sentRes = await api.get('/admin/messages');
      setSent(sentRes.data);
    } catch {
      setError('Error al cargar los datos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    let ignore = false;

    const fetchClients = async () => {
      const query = debouncedClientSearch.trim();
      if (query.length < CLIENT_SEARCH_MIN_CHARS) {
        setClients([]);
        setSearchingClients(false);
        return;
      }

      setSearchingClients(true);
      try {
        const params = new URLSearchParams({
          limit: String(CLIENT_SEARCH_PREVIEW_LIMIT),
          search: query,
        });
        const res = await api.get(`/admin/clients?${params.toString()}`);
        if (!ignore) {
          setClients(res.data);
        }
      } catch {
        if (!ignore) {
          setClients([]);
        }
      } finally {
        if (!ignore) {
          setSearchingClients(false);
        }
      }
    };

    fetchClients();

    return () => {
      ignore = true;
    };
  }, [debouncedClientSearch]);

  const handleSend = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.body.trim()) { setError('El cuerpo del mensaje no puede estar vacío.'); return; }
    setSending(true);
    try {
      await api.post('/admin/messages', {
        recipient_id: form.recipient_id ? parseInt(form.recipient_id) : null,
        subject: form.subject.trim() || null,
        body: form.body.trim(),
      });
      setSuccess('Mensaje enviado correctamente.');
      setForm({ recipient_id: '', subject: '', body: '' });
      setSelectedClient(null);
      setClientSearch('');
      fetchData();
    } catch {
      setError('Error al enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este mensaje?')) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setSent((prev) => prev.filter((m) => m.id !== id));
    } catch {
      setError('Error al eliminar el mensaje.');
    }
  };

  const handleSelectBroadcast = () => {
    setSelectedClient(null);
    setClientSearch('');
    setForm((prev) => ({ ...prev, recipient_id: '' }));
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientSearch(`${client.name} — ${client.email}`);
    setForm((prev) => ({ ...prev, recipient_id: String(client.id) }));
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-24 px-4">
      {/* Compose */}
      <div className="glass-panel rounded-2xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <IoSendOutline className="text-marizan-red" size={22} />
          Nuevo Mensaje
        </h2>

        {error && <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">{error}</p>}
        {success && <p className="text-green-400 text-sm bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-2">{success}</p>}

        <form onSubmit={handleSend} className="space-y-4">
          {/* Destinatario */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Destinatario</label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={handleSelectBroadcast}
                className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${
                  !form.recipient_id
                    ? 'border-blue-400/40 bg-blue-500/10 text-blue-100'
                    : 'border-white/10 bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                <span className="flex items-center gap-2">
                  <IoPeopleOutline size={16} className="text-blue-400" />
                  Todos los clientes
                </span>
                {!form.recipient_id && <span className="text-xs text-blue-300">Seleccionado</span>}
              </button>

              <div className="rounded-xl border border-white/10 bg-zinc-950/70 overflow-hidden">
                <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
                  <IoSearchOutline size={16} className="text-gray-500 flex-shrink-0" />
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setClientSearch(value);
                      if (!value.trim()) {
                        setSelectedClient(null);
                        setForm((prev) => ({ ...prev, recipient_id: '' }));
                      }
                    }}
                    placeholder="Buscar cliente por nombre o correo..."
                    className="w-full bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                  />
                  {clientSearch && (
                    <button
                      type="button"
                      onClick={handleSelectBroadcast}
                      className="text-gray-500 hover:text-white transition-colors"
                      title="Limpiar búsqueda"
                    >
                      <IoCloseCircleOutline size={18} />
                    </button>
                  )}
                </div>

                {selectedClient && form.recipient_id && (
                  <div className="border-b border-white/10 bg-marizan-red/10 px-3 py-2 text-sm text-white">
                    <span className="text-gray-400">Cliente seleccionado:</span>{' '}
                    <span className="font-medium">{selectedClient.name}</span>
                    <span className="text-gray-400"> · {selectedClient.email}</span>
                  </div>
                )}

                <div className="max-h-64 overflow-y-auto">
                  {debouncedClientSearch.trim().length < CLIENT_SEARCH_MIN_CHARS ? (
                    <p className="px-3 py-3 text-sm text-gray-500">
                      Escribe al menos {CLIENT_SEARCH_MIN_CHARS} letras para buscar clientes.
                    </p>
                  ) : searchingClients ? (
                    <p className="px-3 py-3 text-sm text-gray-500">Buscando clientes...</p>
                  ) : clients.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-gray-500">No se encontraron clientes.</p>
                  ) : (
                    clients.map((client) => {
                      const isSelected = String(client.id) === form.recipient_id;
                      return (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className={`w-full border-b border-white/5 px-3 py-3 text-left transition-colors last:border-b-0 ${
                            isSelected
                              ? 'bg-marizan-red/15 text-white'
                              : 'text-gray-200 hover:bg-white/5'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <IoPersonOutline size={16} className={isSelected ? 'text-marizan-red' : 'text-gray-500'} />
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium">{client.name}</p>
                              <p className="truncate text-xs text-gray-400">{client.email}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Asunto */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Asunto (opcional)</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              placeholder="Ej: Actualización de tu plan"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-marizan-red/50"
            />
          </div>

          {/* Cuerpo */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Mensaje *</label>
            <textarea
              rows={5}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Escribe aquí tu mensaje..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-marizan-red/50 resize-none"
              required
            />
          </div>

          <button
            type="submit"
            disabled={sending}
            className="flex items-center gap-2 bg-marizan-red hover:bg-red-700 text-white text-sm font-semibold px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <IoSendOutline size={16} />
            {sending ? 'Enviando…' : 'Enviar Mensaje'}
          </button>
        </form>
      </div>

      {/* Sent list */}
      <div className="space-y-3">
        <h2 className="text-lg font-bold text-white">Mensajes Enviados ({sent.length})</h2>
        {sent.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-8">Todavía no has enviado ningún mensaje.</p>
        )}
        {sent.map((m) => (
          <div key={m.id} className="glass-panel rounded-xl overflow-hidden">
            <button
              className="w-full flex items-start justify-between gap-3 p-4 text-left hover:bg-white/5 transition-colors"
              onClick={() => setExpanded(expanded === m.id ? null : m.id)}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="mt-0.5">
                  {m.recipient_id ? (
                    <IoPersonOutline size={18} className="text-marizan-red flex-shrink-0" />
                  ) : (
                    <IoPeopleOutline size={18} className="text-blue-400 flex-shrink-0" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {m.recipient_id ? m.recipient_name : '📢 Todos los clientes'}
                  </p>
                  {m.subject && <p className="text-gray-300 text-xs truncate">{m.subject}</p>}
                  <p className="text-gray-500 text-xs mt-0.5">{formatDate(m.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {expanded === m.id ? (
                  <IoChevronUpOutline size={16} className="text-gray-400" />
                ) : (
                  <IoChevronDownOutline size={16} className="text-gray-400" />
                )}
              </div>
            </button>
            {expanded === m.id && (
              <div className="px-4 pb-4 space-y-3">
                <p className="text-gray-300 text-sm whitespace-pre-wrap border-t border-white/10 pt-3">{m.body}</p>
                <button
                  onClick={() => handleDelete(m.id)}
                  className="flex items-center gap-1.5 text-red-400 hover:text-red-300 text-xs transition-colors"
                >
                  <IoTrashOutline size={14} /> Eliminar mensaje
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Client view ─────────────────────────────────────────────────────────────

function ClientMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get('/me/messages');
      setMessages(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleOpen = async (msg) => {
    const isNowOpen = expanded === msg.id;
    setExpanded(isNowOpen ? null : msg.id);

    if (!isNowOpen && !msg.is_read) {
      try {
        await api.post(`/me/messages/${msg.id}/read`);
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, is_read: true } : m))
        );
      } catch {
        // silently fail
      }
    }
  };

  const unreadCount = messages.filter((m) => !m.is_read).length;

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-24 px-4">
      {unreadCount > 0 && (
        <div className="flex items-center gap-2 bg-marizan-red/10 border border-marizan-red/20 rounded-xl px-4 py-3">
          <IoMailOutline size={18} className="text-marizan-red flex-shrink-0" />
          <p className="text-marizan-red text-sm font-medium">
            Tienes {unreadCount} mensaje{unreadCount > 1 ? 's' : ''} sin leer
          </p>
        </div>
      )}

      {messages.length === 0 && (
        <p className="text-gray-500 text-sm text-center py-12">No tienes mensajes del coach todavía.</p>
      )}

      {messages.map((m) => (
        <div
          key={m.id}
          className={`glass-panel rounded-xl overflow-hidden transition-all ${!m.is_read ? 'border border-marizan-red/30' : ''}`}
        >
          <button
            className="w-full flex items-start gap-3 p-4 text-left hover:bg-white/5 transition-colors"
            onClick={() => handleOpen(m)}
          >
            <div className="mt-0.5 flex-shrink-0">
              {m.is_read ? (
                <IoMailOpenOutline size={20} className="text-gray-400" />
              ) : (
                <IoMailOutline size={20} className="text-marizan-red" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className={`text-sm font-semibold truncate ${m.is_read ? 'text-gray-300' : 'text-white'}`}>
                  {m.subject || 'Mensaje del Coach'}
                  {!m.is_read && (
                    <span className="ml-2 inline-block w-2 h-2 rounded-full bg-marizan-red align-middle" />
                  )}
                </p>
                {m.is_read && <IoCheckmarkDoneOutline size={14} className="text-gray-500 flex-shrink-0" />}
              </div>
              <p className="text-gray-400 text-xs mt-0.5">
                {m.recipient_id ? 'Mensaje directo' : '📢 Mensaje para todos'}
                {' · '}
                {formatDate(m.created_at)}
              </p>
            </div>
            <div className="flex-shrink-0 mt-0.5">
              {expanded === m.id ? (
                <IoChevronUpOutline size={16} className="text-gray-400" />
              ) : (
                <IoChevronDownOutline size={16} className="text-gray-400" />
              )}
            </div>
          </button>

          {expanded === m.id && (
            <div className="px-4 pb-4 border-t border-white/10 pt-3">
              <p className="text-gray-200 text-sm whitespace-pre-wrap">{m.body}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Root export ─────────────────────────────────────────────────────────────

const Messages = () => {
  const { user } = useAuthContext();
  const isCoach = ['coach', 'admin'].includes(user?.role);

  return (
    <div className="container mx-auto pt-6">
      <h1 className="text-2xl font-bold text-white mb-6 px-4">
        {isCoach ? 'Mensajes a Clientes' : 'Mensajes del Coach'}
      </h1>
      {isCoach ? <CoachMessages /> : <ClientMessages />}
    </div>
  );
};

export default Messages;
